import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { PermissionsEnum } from "@/auth/interfaces";
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTime,
  hasPermission,
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from "@/shared";
import { useFetchIvas, useUpdateIva } from "@/shared/actions/erp";
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from "@/shared/components/ui";
import { IVA } from "@/shared/interfaces";
import { useUiConfirmModalStore } from "@/store/ui";

export interface IvasPageInterface {}

const IvasPage: React.FC<IvasPageInterface> = () => {
  const returnUrlIvaPage = ROUTER_PATHS.administration.ivaNav;

  useCheckPermissions(PermissionsEnum.api_view_iva);

  const navigate = useNavigate();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* fetch data
  const {
    data: ivasPagingRes,
    isLoading,
    isRefetching,
  } = useFetchIvas({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });


  ///* mutations
  const updateIvaMutation = useUpdateIva(navigate, returnUrlIvaPage);

  ///* handlers
  const onEdit = (original: IVA) => {
    setConfirmDialog({
      isOpen: true,
      title: "Editar IVA",
      subtitle: "¿Está seguro que desea editar este registro?",
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(`${returnUrlIvaPage}/editar/${original.id_iva}`);
      },
    });
  };

  console.log("Filtrop tabla", columnFilters )
  ///* columns
  const columns = useMemo<MRT_ColumnDef<IVA>[]>(
    () => [
      {
        accessorKey: "nombre",
        header: "Nombre",
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, "nombre"),
      },
      {
        accessorKey: "porcentaje",
        header: "Porcentaje",
        size: 180,
        Cell: ({ row }) =>
          row.original?.porcentaje ? `${row.original.porcentaje} %` : "N/A",
      },
      // {
      //   accessorKey: 'codigo',
      //   header: 'Código',
      //   size: 180,
      //   Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      // },
      {
        accessorKey: "numero_referencia",
        header: "Número de Referencia",
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, "numero_referencia"),
      },
      {
        accessorKey: "sri_codigo",
        header: "SRI Código",
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, "sri_codigo"),
      },
      {
        accessorKey: "sri_codigo_porcentaje",
        header: "SRI Código Porcentaje",
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, "sri_codigo_porcentaje"),
      },

      {
        accessorKey: "estado",
        header: "Estado",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado}
            titulo={row.original.estado ? "Activo" : "Inactivo"}
            setEstadoSwitch={(iva) => {
              if (!hasPermission(PermissionsEnum.api_change_iva)) return;

              setConfirmDialog({
                isOpen: true,
                title: "Cambiar Estado",
                subtitle:
                  "¿Está seguro que desea cambiar el estado de este registro?",
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  updateIvaMutation.mutate({
                    id: row.original.id_iva!,
                    data: {
                      ...row.original,
                      estado: iva as boolean,
                    },
                  });
                },
              });
            }}
          />
        ),
      },
      {
        accessorKey: "fecha_creacion",
        header: "Fecha de Creación",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_creacion
            ? formatDateWithTime(row.original.fecha_creacion)
            : "N/A",
      },
      {
        accessorKey: "fecha_modificacion",
        header: "Fecha de Modificación",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_modificacion
            ? formatDateWithTime(row.original.fecha_modificacion)
            : "N/A",
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene
      title="IVA"
      urlRedirect={`${returnUrlIvaPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_iva)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="IVA por nombre"
        cleanFilter={cleanFilter}
      />

      <GeneralTable<IVA>
        columns={columns}
        data={ivasPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        //enableSorting={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={ivasPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasPermission(PermissionsEnum.api_change_iva)}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        positionActionsColumn="first"
        // crud
        canEdit={hasPermission(PermissionsEnum.api_change_iva)}
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default IvasPage;
